import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SymbolvalidatorService } from '@services/filevalidators/symbolvalidator.service';
import { NotificationService } from '@services/notification.service';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { TransferErrorPopupComponent } from '@shared/components/transfer-error-popup/transfer-error-popup.component';
import { OptionalField } from '@shared/models/fileupload';
import { GenericTableColumn } from '@shared/models/generictablecolumn';
import { SYMBOL_AWS_ENV_IMPORT_EXPORT_PERMISSION, SymbolTransferService } from '@shared/services/export-import-services/symbol';
import { ImportValidatorService } from '@shared/services/filevalidators/Importvalidator.service';
import { ISymbolGetResponse, SymbolService } from '@shared/services/symbol.service';
import { SelectSubMenuItem } from '@shared/utils/SelectSubMenuItemManager';
import { catchError, finalize, map, Observable, Subject, takeUntil, tap, throwError } from 'rxjs';
import { registerNames } from '../menu';

@Component({
	selector: 'app-singleregister',
	templateUrl: './singleregister.component.html',
	styleUrls: ['./singleregister.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class SingleregisterComponent implements OnInit, OnDestroy {
	public readableRegisterNames: object = registerNames;
	public registerName: string = '';
	public isLoading: boolean = false;
	public showSearch: boolean = false;
	public selected: object[] = [];
	public registerData: Observable<ISymbolGetResponse[]>;
	public registerFilter: string = '';
	public totalCount: number;
	public loadingSubject: Subject<boolean> = new Subject<boolean>();
	private unsubscribe: Subject<void> = new Subject<void>();
	public countSubject$: Subject<number> = new Subject<number>();
	public count$ = this.countSubject$.asObservable();

	public registerTableColumns: GenericTableColumn[] = [
		{
			name: 'masterdata.symbols.table.preview',
			dataKey: 'downloadUrl',
			isImg: true,
			isLink: false,
			linkTitle: 'masterdata.symbols.table.previewLink',
		},
		{ name: 'masterdata.symbols.table.symbolname', dataKey: 'name' },
		{ name: 'masterdata.symbols.table.language', dataKey: 'language' },
		{
			name: 'masterdata.symbols.table.description',
			dataKey: 'description',
		},
		{ name: 'masterdata.symbols.table.width', dataKey: 'width' },
		{ name: 'masterdata.symbols.table.height', dataKey: 'height' },
		{ name: 'masterdata.symbols.table.basepointx', dataKey: 'basePointX' },
		{ name: 'masterdata.symbols.table.basepointy', dataKey: 'basePointY' },
	];

	public regRowActions: object[] = [
		{
			title: 'masterdata.symbols.table.actions.edit',
			handler: 'editMasterdataRow',
		},
		{
			title: 'masterdata.symbols.table.actions.delete',
			handler: 'deleteMasterdataRow',
		},
	];

	public selectActions: SelectSubMenuItem[] = [
		{
			icon: () => 'acies_upload',
			text: () => 'masterdata.symbols.export.button',
			click: (data) => this.handleExportMasterData(data),
		},
		{
			icon: () => 'acies_upload',
			text: () => 'masterdata.symbols.export.externalButton',
			click: (data) => this.handleExportMasterData(data, true),
			// Both are needed - the list holds every permission asked for.
			permissions: () => [SYMBOL_AWS_ENV_IMPORT_EXPORT_PERMISSION, 'drawing:SymbolCreate'],
		},
	];

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_add_alt',
			text: () => 'masterdata.symbols.upload.button',
			click: () => this.openUploadSymbol(),
		},
	];

	// Import and its template go in the overflow menu, where the export of the selected symbols already is.
	menuItems: SubMenuItem[] = [
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.symbols.import.button',
			click: () => this.handleImportMasterData(),
			permissions: () => ['drawing:SymbolCreate'],
		},
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.symbols.import.template.button',
			click: () => this.symbolTransferService.downloadTemplate(),
			permissions: () => ['drawing:SymbolCreate'],
		},
	];

	constructor(
		private activatedRoute: ActivatedRoute,
		public dialog: MatDialog,
		private injector: Injector,
		private translator: TranslateService,
		private symbolService: SymbolService,
		private notificationService: NotificationService,
		private symbolTransferService: SymbolTransferService,
		private importValidatorService: ImportValidatorService,
	) {}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
		this.loadingSubject.complete();
		this.countSubject$.complete();
	}

	ngOnInit(): void {
		this.activatedRoute.params.subscribe((urlData) => {
			this.registerName = urlData['registerName'];
			this.loadData();
		});
	}

	loadData(_: number = 0) {
		this.isLoading = true;
		this.registerData = this.symbolService.getAll().pipe(
			takeUntil(this.unsubscribe),
			tap((data) => {
				this.isLoading = true;
				this.countSubject$.next(data.result.length);
			}),
			map((data) => data.result),
			finalize(() => {
				this.isLoading = false;
			}),
		);
	}

	handleSelected(listOfSelected: object[]): void {
		this.selected = listOfSelected;
		console.log('Selected:', this.selected);
	}

	handleRowMenuAction(data: object): void {
		switch (data['action'].handler) {
			case 'editMasterdataRow':
				console.log(data);
				this.openUploadSymbol(data['data']);
				break;
			case 'deleteMasterdataRow':
				this.onDeleteSymbol(data['data']);
				break;
			case 'exportMasterData':
				this.handleExportMasterData(data['data']);
				break;
			default:
				break;
		}
	}

	handleExportMasterData(data: ISymbolGetResponse[], external: boolean = false): void {
		this.symbolTransferService.exportData(data, external).subscribe({
			next: () => {
				this.notificationService.notify(['masterdata.symbols.export.notification.success.header'], ['masterdata.symbols.export.notification.success.content'], 'success');
			},
			error: (error: Error) => {
				this.notificationService.notify(['masterdata.symbols.export.notification.error.header'], ['masterdata.symbols.export.notification.error.content'], 'error');
				this.dialog.open(TransferErrorPopupComponent, {
					width: '450px',
					data: { message: error.message },
				});
			},
		});
	}

	handleImportMasterData(): void {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'masterdata.symbols.import.title',
				subHeadingKey: 'masterdata.symbols.import.subtitle',
				confirmButtonKey: 'masterdata.symbols.import.button',
				notificationHeader: 'masterdata.symbols.import.notification.success.header',
				notificationContent: 'masterdata.symbols.import.notification.success.content',
				notificationErrorHeader: 'masterdata.symbols.import.notification.error.header',
				notificationErrorContent: 'masterdata.symbols.import.notification.error.content',
				notificationClientErrorHeader: 'masterdata.symbols.import.notification.clientError.header',
				notificationClientErrorContent: 'masterdata.symbols.import.notification.clientError.content',
				detailedClientErrorMessage: 'masterdata.symbols.import.detailed-client-error-message',
				allowedFileTypes: ['.xlsx'],
				validatorService: this.importValidatorService,
				data: this.symbolTransferService,
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(() => {
				this.loadData();
			});
	}

	handleReloadData(_: object): void {
		// Empty function to enable client side only pagination
		// this.loadData(data['pageSize'] * data['pageNumber']);
	}

	openUploadSymbol(symbolData: object = null) {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			disableClose: true, // Luk ikke ved tryk på ESC eller backdrop - træls at miste indtastet data i alle felter
			width: '650px',
			data: {
				headingKey: symbolData ? 'masterdata.symbols.upload.editheading' : 'masterdata.symbols.upload.heading',
				subHeadingKey: 'masterdata.symbols.upload.subheading',
				allowedFileTypes: ['.svg', '.jpg', '.png'],
				validatorService: this.injector.get(SymbolvalidatorService),
				loadingHandler: this.loadingSubject,
				mode: symbolData ? 'edit' : 'create',
				data: symbolData,
				optionalFields: [
					{
						name: 'symbol_name',
						titleKey: 'masterdata.symbols.upload.title',
						mandatory: true,
						value: symbolData && symbolData['name'] ? symbolData['name'] : '',
						class: 'w75',
						// readonly: symbolData && symbolData['name'],
						validatorPattern: /^[\w\-æøå]+$/i,
						errorMessage: 'masterdata.symbols.upload.titleError',
					},
					{
						name: 'symbol_language',
						titleKey: 'masterdata.symbols.upload.language',
						class: 'w25',
						value: symbolData && symbolData['language'] ? symbolData['language'] : '',
					},
					{
						name: 'symbol_description',
						titleKey: 'masterdata.symbols.upload.description',
						mandatory: true,
						value: symbolData && symbolData['description'] ? symbolData['description'] : '',
					},
					{
						name: 'symbol_width',
						titleKey: 'masterdata.symbols.upload.width',
						mandatory: true,
						type: 'number',
						class: 'w25',
						value: symbolData && symbolData['width'] ? symbolData['width'] : '',
					},
					{
						name: 'symbol_height',
						titleKey: 'masterdata.symbols.upload.height',
						mandatory: true,
						type: 'number',
						class: 'w25',
						value: symbolData && symbolData['height'] ? symbolData['height'] : '',
					},
					{
						name: 'symbol_refwidth',
						titleKey: 'masterdata.symbols.upload.refwidth',
						mandatory: true,
						type: 'number',
						class: 'w25',
						value: symbolData && symbolData['basePointX'] ? symbolData['basePointX'] : '0',
						minimumValue: false,
					},
					{
						name: 'symbol_refheight',
						titleKey: 'masterdata.symbols.upload.refheight',
						mandatory: true,
						type: 'number',
						class: 'w25',
						value: symbolData && symbolData['basePointY'] ? symbolData['basePointY'] : '0',
						minimumValue: false,
					},
					{
						name: 'symbol_isDefault',
						titleKey: 'masterdata.symbols.upload.isDefault',
						type: 'checkbox',
						value: symbolData && symbolData['isDefault'] !== undefined ? symbolData['isDefault'] : false,
					},
				] as OptionalField[], // Typecast this JSON property (array) to an array of the interface used in validator service to get autocomplete in IDE
				processFileHook() {
					// Function injected to run when the file list is prepared, in this case populating the name and size fields
					const URL = window.URL || window.webkitURL;
					const theFile = this.files[0];
					const dummyImg = new Image();
					dummyImg.src = URL.createObjectURL(theFile);

					if (!symbolData) {
						this.formData['symbol_name'].setValue(theFile.name.split('.').shift());
					}

					dummyImg.onload = function () {
						this.formData['symbol_width'].setValue(dummyImg.width);
						this.formData['symbol_height'].setValue(dummyImg.height);
					}.bind(this); // Bind "this" in function to the angular component, not the image

					dummyImg.onerror = function () {
						console.log('Could not load image to get the image size');
					};
				},
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((result) => {
				if (result != null) {
					this.isLoading = true;
					this.loadData();
				}
			});
	}

	private handleError(error: HttpErrorResponse) {
		if (error.status === 0) {
			// A client-side or network error occurred. Handle it accordingly.
			console.error('An error occurred:', error.error);
		} else {
			// The backend returned an unsuccessful response code.
			// The response body may contain clues as to what went wrong.
			console.error(`Backend returned code ${error.status}, body was: `, error.error);
		}
		// Return an observable with a user-facing error message.
		return throwError(() => new Error('Something bad happened; please try again later.'));
	}

	onDeleteSymbol(symbol: ISymbolGetResponse) {
		const dialogRef = this.dialog.open(ConfirmdialogComponent, {
			width: '450px',
			data: {
				okReturnData: true,
				cancelReturnData: false,
				heading: this.translator.instant('masterdata.symbols.delete.deleteDialogHeading'),
				content: this.translator.instant('masterdata.symbols.delete.deleteDialogContent', {
					name: symbol.name,
				}),
				okButtonText: this.translator.instant('general.ok'),
				cancelButtonText: this.translator.instant('general.cancel'),
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((result) => {
				if (result === true) {
					this.selected = [];
					this.symbolService
						.delete(symbol.name, symbol.language)
						.pipe(catchError(this.handleError))
						.subscribe(() => {
							this.notificationService.notify(
								['masterdata.symbols.delete.deletedNotificationHeader'],
								[
									'masterdata.symbols.delete.deletedNotificationContent',
									{
										name: symbol.name,
									},
								],
								'success',
							);
							this.loadData();
						});
				}
			});
	}
}
